import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { api, post, patch, del, ApiError } from '../../lib/api.ts';
import type { VersionDetail } from '../../lib/policy/api.ts';
import { PolicyEditor } from './PolicyEditor.tsx';
import { ErrorText } from '../../components/AuthShell.tsx';
import { FormSection, FormField, ftInput } from '../../components/ui/FormTable.tsx';
import { Button, buttonClass } from '../../components/ui/Button.tsx';
import { dialog } from '../../components/ui/Dialog.tsx';
import { fmtTime } from '../../lib/date.ts';

const btnGhost = buttonClass('secondary');

/** 초안 편집: 2초 자동 저장(낙관적 잠금) · 개정 요청 제출 */
export function PolicyVersionEditPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['policy-version', id], queryFn: () => api<VersionDetail>(`/policies/versions/${id}`), staleTime: Infinity });
  const [status, setStatus] = useState('');
  const [conflict, setConflict] = useState(false);
  const updatedAt = useRef<string>('');
  const pending = useRef<unknown>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => { if (q.data) updatedAt.current = q.data.updatedAt; }, [q.data]);

  async function flush() {
    if (pending.current == null) return;
    const body = pending.current; pending.current = null;
    setStatus('저장 중…');
    try {
      const r = await patch<{ updatedAt: string; clauseCount: number }>(`/policies/versions/${id}`, { body, expectedUpdatedAt: updatedAt.current });
      updatedAt.current = r.updatedAt; setStatus(`저장됨 · 조항 ${r.clauseCount}개 · ${fmtTime(new Date())}`);
    } catch (e) { if (e instanceof ApiError && e.status === 409) { setConflict(true); setStatus('충돌: 다른 곳에서 먼저 저장됨'); } else setStatus(`저장 실패: ${(e as Error).message}`); }
  }
  function onChange(json: unknown) { pending.current = json; setStatus('변경됨…'); if (timer.current) window.clearTimeout(timer.current); timer.current = window.setTimeout(flush, 2000); }
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const [summary, setSummary] = useState(''); const [eff, setEff] = useState('');
  const submit = useMutation({ mutationFn: async () => { if (timer.current) window.clearTimeout(timer.current); await flush(); return post<{ id: string }>(`/policies/documents/${q.data!.documentId}/changes`, { changeSummary: summary, effectiveDate: eff || undefined }); }, onSuccess: (cr) => { qc.invalidateQueries({ queryKey: ['policy-doc'] }); nav(`/policies/changes/${cr.id}`); } });
  const discard = useMutation({ mutationFn: () => del(`/policies/versions/${id}`), onSuccess: () => nav(`/policies/${q.data!.documentId}`) });

  if (q.isLoading) return <div>불러오는 중…</div>;
  if (q.isError || !q.data) return <div className="text-sm text-red-600">{(q.error as Error)?.message}</div>;
  const v = q.data;
  if (!v.canEdit) return <div className="text-sm">이 버전은 편집할 수 없습니다 ({v.status}). <Link className="text-blue-700 hover:underline" to={`/policies/${v.documentId}`}>문서로</Link></div>;
  return (
    <div className="">
      <PageHeader back={{ to: `/policies/${v.documentId}`, label: `${v.document.title}` }} title={<>{v.document.title} — {v.versionNo ? `v${v.versionNo}` : '초안'} 편집</>} actions={<><div className="text-xs text-slate-500">{status}</div></>} />
      {conflict && <div className="mb-3 border border-red-200 bg-red-50 p-2 text-sm">다른 곳에서 먼저 저장되었습니다. <button className="text-blue-700 hover:underline" onClick={() => location.reload()}>새로고침</button> 후 다시 편집하세요. (실시간 공동편집은 후속 CR)</div>}
      <PolicyEditor initial={v.body} onChange={onChange} editable={!conflict} />
      <section className="mt-5 border border-slate-200 p-3">
        <h2 className="font-semibold text-sm mb-2">개정 요청 제출 (결재)</h2>
        <FormSection>
          <FormField label="개정 사유" required><input className={ftInput + ' ft-w-lg'} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="예: 안전성 확보조치 기준 개정 반영 (제5조·제8조)" /></FormField>
          <FormField label="시행일" hint="비우면 승인일"><input type="date" className={ftInput + ' ft-w-sm'} value={eff} onChange={(e) => setEff(e.target.value)} /></FormField>
        </FormSection>
        <div className="flex gap-2"><Button busy={submit.isPending} disabled={!summary.trim() || conflict} onClick={() => submit.mutate()}>결재 올리기</Button><button className={btnGhost} onClick={async () => { if (await dialog.confirm('초안을 폐기하시겠습니까? 되돌릴 수 없습니다.', { title: '초안 폐기', danger: true, okText: '폐기' })) discard.mutate(); }}>초안 폐기</button></div>
        <ErrorText error={submit.error ?? discard.error} />
      </section>
    </div>
  );
}
